
(() => {
  'use strict';

  const MM = 25.4;
  const QTY = [
    1, 2, 3, 4, 5, 10, 15, 20, 25, 30,
    40, 50, 70, 100, 150, 200, 250,
    300, 350, 400, 500, 700, 1000
  ];
  const CUT = {
    line: [40, 100, 300],
    shape: [40, 100, 250]
  };
  const f = n => Number(n.toFixed(4));

  function buildLayout(o, marks, sections, rotated, staggered) {
    const {sw, sh, w, h, bleed, gap, shape} = o;
    const bw = (rotated ? h : w) + 2 * bleed;
    const bh = (rotated ? w : h) + 2 * bleed;
    const mx = marks === 'left-right' ? 12 : 5;
    const my = marks === 'top-bottom' ? 12 : 5;
    const left = mx, right = sw - mx;
    const top = my, bottom = sh - my;
    const usableW = right - left;
    const lanes = [];

    if (sections === 2) {
      lanes.push([sh / 2 - 3, sh / 2 + 3]);
    }
    if (sections === 3) {
      lanes.push(
        [sh / 3 - 3, sh / 3 + 3],
        [2 * sh / 3 - 3, 2 * sh / 3 + 3]
      );
    }

    const bands = [];
    let start = top;
    for (const [a, b] of lanes) {
      bands.push([start, Math.min(a, bottom)]);
      start = Math.max(start, b);
    }
    bands.push([start, bottom]);

    const placements = [];

    for (let bandIndex = 0; bandIndex < bands.length; bandIndex++) {
      const [a, b] = bands[bandIndex];
      const height = b - a;

      if (height < bh || usableW < bw) continue;

      if (!staggered) {
        const cols = Math.floor(
          (usableW + gap + 1e-8) / (bw + gap)
        );
        const rows = Math.floor(
          (height + gap + 1e-8) / (bh + gap)
        );
        if (cols < 1 || rows < 1) continue;

        const blockW = cols * bw + (cols - 1) * gap;
        const blockH = rows * bh + (rows - 1) * gap;
        const x0 = left + (usableW - blockW) / 2;
        const y0 = a + (height - blockH) / 2;

        for (let r = 0; r < rows; r++) {
          for (let c = 0; c < cols; c++) {
            placements.push({
              x: x0 + c * (bw + gap),
              y: y0 + r * (bh + gap),
              band: bandIndex
            });
          }
        }
      } else {
        const pitch = (bh + gap) * Math.sqrt(3) / 2;
        const rows = 1 + Math.floor(
          (height - bh + 1e-8) / pitch
        );
        const totalH = bh + (rows - 1) * pitch;
        const y0 = a + (height - totalH) / 2;

        for (let r = 0; r < rows; r++) {
          const offset = r % 2 ? (bw + gap) / 2 : 0;
          const cols = usableW >= bw + offset
            ? 1 + Math.floor(
                (usableW - bw - offset + 1e-8) / (bw + gap)
              )
            : 0;
          if (cols < 1) continue;

          const rowW = cols * bw + (cols - 1) * gap;
          const x0 = left + (usableW - rowW) / 2;

          for (let c = 0; c < cols; c++) {
            placements.push({
              x: x0 + c * (bw + gap),
              y: y0 + r * pitch,
              band: bandIndex
            });
          }
        }
      }
    }

    if (placements.some(p =>
      p.x < left - 1e-6 ||
      p.x + bw > right + 1e-6 ||
      p.y < top - 1e-6 ||
      p.y + bh > bottom + 1e-6 ||
      lanes.some(([a, b]) =>
        p.y < b - 1e-6 && p.y + bh > a + 1e-6
      )
    )) return null;

    return {
      ...o, marks, sections, rotated, staggered,
      bw, bh, lanes, placements, ups: placements.length
    };
  }

  function chooseLayout(o) {
    const marksOptions = o.vulcan
      ? ['top-bottom', 'left-right']
      : ['off'];
    const layouts = [];

    for (const marks of marksOptions) {
      for (const sections of (o.transport ? [1, 2, 3] : [1])) {
        for (const rotated of [false, true]) {
          const staggerOptions =
            o.shape === 'round' || o.shape === 'oval'
              ? [false, true]
              : [false];

          for (const staggered of staggerOptions) {
            const L = buildLayout(
              o, marks, sections, rotated, staggered
            );
            if (L && L.ups > 0) layouts.push(L);
          }
        }
      }
    }

    const full = layouts
      .filter(x => x.sections === 1)
      .sort((a, b) => b.ups - a.ups)[0];

    if (!full) return null;

    const eligible = layouts.filter(
      x => x.ups >= full.ups * 0.85
    );
    eligible.sort((a, b) =>
      b.sections - a.sections || b.ups - a.ups
    );

    return {...eligible[0], fullUps: full.ups};
  }

  function shapeSVG(x, y, w, h, r, shape, color) {
    if (shape !== 'rectangle') {
      return `<ellipse cx="${f(x + w / 2)}"
        cy="${f(y + h / 2)}"
        rx="${f(w / 2)}" ry="${f(h / 2)}"
        fill="none" stroke="${color}"
        stroke-width=".35"/>`;
    }

    return `<rect x="${f(x)}" y="${f(y)}"
      width="${f(w)}" height="${f(h)}"
      rx="${f(r)}" fill="none"
      stroke="${color}" stroke-width=".35"/>`;
  }

  function preview(root, L, radius, vulcan) {
    const box = root.querySelector('[data-sc-preview]');
    if (!box) return;

    const fw = L.rotated ? L.h : L.w;
    const fh = L.rotated ? L.w : L.h;

    let paths = L.placements.map(p =>
      shapeSVG(
        p.x, p.y, L.bw, L.bh, 0, L.shape, '#222'
      ) +
      shapeSVG(
        p.x + L.bleed, p.y + L.bleed,
        fw, fh,
        L.shape === 'rectangle' ? radius : 0,
        L.shape, '#df3030'
      )
    ).join('');

    paths += L.lanes.map(([a, b]) =>
      `<rect x="0" y="${f(a)}"
        width="${f(L.sw)}" height="${f(b - a)}"
        fill="#f59e0b" fill-opacity=".18"
        stroke="#d97706" stroke-dasharray="3 2"
        stroke-width=".5"/>`
    ).join('');

    if (vulcan) {
      for (const [x, y] of [
        [7.5, 7.5], [L.sw - 7.5, 7.5],
        [7.5, L.sh - 7.5], [L.sw - 7.5, L.sh - 7.5]
      ]) {
        paths += `<circle cx="${f(x)}" cy="${f(y)}"
          r="2.5" fill="black"/>`;
      }

      const x = L.sw - 14.5, y = L.sh - 6.5;
      paths += `<polygon points="
        ${f(x - 1.5)},${f(y + 2)}
        ${f(x + 1.5)},${f(y + 2)}
        ${f(x)},${f(y)}" fill="black"/>`;
    }

    box.innerHTML =
      `<svg xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 ${f(L.sw)} ${f(L.sh)}">
        <rect width="${f(L.sw)}" height="${f(L.sh)}"
          fill="white" stroke="#777" stroke-width="1"/>
        ${paths}
      </svg>`;
  }

  function pdfPath(x, y, w, h, r, shape, sh) {
    const Y = v => sh - v;
    const k = .5522847498;

    if (shape !== 'rectangle') {
      const cx = x + w / 2;
      const cy = y + h / 2;
      const rx = w / 2;
      const ry = h / 2;

      return [
        `${f(cx + rx)} ${f(Y(cy))} m`,
        `${f(cx + rx)} ${f(Y(cy - ry*k))} ${f(cx + rx*k)} ${f(Y(cy - ry))} ${f(cx)} ${f(Y(cy - ry))} c`,
        `${f(cx - rx*k)} ${f(Y(cy - ry))} ${f(cx - rx)} ${f(Y(cy - ry*k))} ${f(cx - rx)} ${f(Y(cy))} c`,
        `${f(cx - rx)} ${f(Y(cy + ry*k))} ${f(cx - rx*k)} ${f(Y(cy + ry))} ${f(cx)} ${f(Y(cy + ry))} c`,
        `${f(cx + rx*k)} ${f(Y(cy + ry))} ${f(cx + rx)} ${f(Y(cy + ry*k))} ${f(cx + rx)} ${f(Y(cy))} c`,
        'h S'
      ].join('\n') + '\n';
    }

    if (!r) {
      return `${f(x)} ${f(Y(y + h))} ${f(w)} ${f(h)} re S\n`;
    }

    r = Math.min(r, w / 2, h / 2);

    return [
      `${f(x + r)} ${f(Y(y))} m`,
      `${f(x + w - r)} ${f(Y(y))} l`,
      `${f(x + w - r + r*k)} ${f(Y(y))} ${f(x + w)} ${f(Y(y + r - r*k))} ${f(x + w)} ${f(Y(y + r))} c`,
      `${f(x + w)} ${f(Y(y + h - r))} l`,
      `${f(x + w)} ${f(Y(y + h - r + r*k))} ${f(x + w - r + r*k)} ${f(Y(y + h))} ${f(x + w - r)} ${f(Y(y + h))} c`,
      `${f(x + r)} ${f(Y(y + h))} l`,
      `${f(x + r - r*k)} ${f(Y(y + h))} ${f(x)} ${f(Y(y + h - r + r*k))} ${f(x)} ${f(Y(y + h - r))} c`,
      `${f(x)} ${f(Y(y + r))} l`,
      `${f(x)} ${f(Y(y + r - r*k))} ${f(x + r - r*k)} ${f(Y(y))} ${f(x + r)} ${f(Y(y))} c`,
      'h S'
    ].join('\n') + '\n';
  }

  function continuousCutPaths(L) {
    const fmt = n => Number(n.toFixed(4));
    const Y = y => L.sh - y;
    const ext = 0.5;
    const fw = L.rotated ? L.h : L.w;
    const fh = L.rotated ? L.w : L.h;
    const bands = new Map();

    for (const p of L.placements) {
      const band = p.band ?? 0;
      if (!bands.has(band)) bands.set(band, []);

      bands.get(band).push({
        x1: p.x + L.bleed,
        y1: p.y + L.bleed,
        x2: p.x + L.bleed + fw,
        y2: p.y + L.bleed + fh
      });
    }

    let output = '';

    for (const stickers of bands.values()) {
      if (!stickers.length) continue;

      const left = Math.min(...stickers.map(p => p.x1));
      const right = Math.max(...stickers.map(p => p.x2));
      const top = Math.min(...stickers.map(p => p.y1));
      const bottom = Math.max(...stickers.map(p => p.y2));
      const horizontal = new Set();
      const vertical = new Set();

      for (const p of stickers) {
        horizontal.add(fmt(p.y1));
        horizontal.add(fmt(p.y2));
        vertical.add(fmt(p.x1));
        vertical.add(fmt(p.x2));
      }

      for (const y of [...horizontal].sort((a, b) => a - b)) {
        output +=
          `${fmt(left - ext)} ${fmt(Y(y))} m\n` +
          `${fmt(right + ext)} ${fmt(Y(y))} l S\n`;
      }

      for (const x of [...vertical].sort((a, b) => a - b)) {
        output +=
          `${fmt(x)} ${fmt(Y(top - ext))} m\n` +
          `${fmt(x)} ${fmt(Y(bottom + ext))} l S\n`;
      }
    }

    return output;
  }

  function makePDF(L, radius, vulcan) {
    const pt = 72 / MM;
    const W = f(L.sw * pt);
    const H = f(L.sh * pt);
    const fw = L.rotated ? L.h : L.w;
    const fh = L.rotated ? L.w : L.h;
    const commands = [];

    for (let page = 1; page <= 2; page++) {
      let s = `q\n${f(pt)} 0 0 ${f(pt)} 0 0 cm\n0.25 w\n`;
      s += page === 1 ? '0 0 0 RG\n' : '1 0 0 RG\n';

      if (
        page === 2 &&
        L.shape === 'rectangle' &&
        radius === 0
      ) {
        s += continuousCutPaths(L);
      } else {
        for (const p of L.placements) {
          s += page === 1
            ? pdfPath(
                p.x, p.y, L.bw, L.bh,
                0, L.shape, L.sh
              )
            : pdfPath(
                p.x + L.bleed,
                p.y + L.bleed,
                fw, fh, radius, L.shape, L.sh
              );
        }
      }

      if (vulcan) {
        s += '0 0 0 rg\n';

        for (const [x, y] of [
          [7.5, 7.5], [L.sw - 7.5, 7.5],
          [7.5, L.sh - 7.5], [L.sw - 7.5, L.sh - 7.5]
        ]) {
          s += pdfPath(
            x - 2.5, y - 2.5, 5, 5, 0, 'round', L.sh
          ).replace(/h S\n$/, 'h f\n');
        }

        if (page === 1) {
          const x = L.sw - 14.5;
          const y = L.sh - 6.5;
          s += `${f(x - 1.5)} ${f(L.sh - y - 2)} m\n`;
          s += `${f(x + 1.5)} ${f(L.sh - y - 2)} l\n`;
          s += `${f(x)} ${f(L.sh - y)} l h f\n`;
        }
      }

      commands.push(s + 'Q\n');
    }

    const objs = [];
    const add = s => (objs.push(s), objs.length);
    const catalog = add('');
    const pages = add('');
    const pageIds = [];

    for (const content of commands) {
      const stream = add(
        `<< /Length ${content.length} >>\nstream\n${content}endstream`
      );

      pageIds.push(add(
        `<< /Type /Page /Parent ${pages} 0 R
        /MediaBox [0 0 ${W} ${H}]
        /Contents ${stream} 0 R /Resources << >> >>`
      ));
    }

    objs[catalog - 1] =
      `<< /Type /Catalog /Pages ${pages} 0 R >>`;

    objs[pages - 1] =
      `<< /Type /Pages /Kids [${pageIds.map(id => id + ' 0 R').join(' ')}] /Count 2 >>`;

    let pdf = '%PDF-1.4\n';
    const offsets = [0];

    objs.forEach((obj, i) => {
      offsets.push(pdf.length);
      pdf += `${i + 1} 0 obj\n${obj}\nendobj\n`;
    });

    const xref = pdf.length;
    pdf += `xref\n0 ${objs.length + 1}\n0000000000 65535 f \n`;

    offsets.slice(1).forEach(n => {
      pdf += String(n).padStart(10, '0') + ' 00000 n \n';
    });

    pdf += `trailer\n<< /Size ${objs.length + 1} /Root ${catalog} 0 R >>\n`;
    pdf += `startxref\n${xref}\n%%EOF`;

    const url = URL.createObjectURL(
      new Blob([pdf], {type: 'application/pdf'})
    );

    const a = document.createElement('a');
    a.href = url;
    a.download =
      `Sticker_Imposition_${Math.round(L.sw / MM)}x${Math.round(L.sh / MM)}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();

    setTimeout(() => URL.revokeObjectURL(url), 5000);
  }

  function calculate(root) {
    const get = k =>
      root.querySelector(`[data-sc="${k}"]`)?.value;
    const num = k => Number(get(k));
    const factor = get('unit') === 'in' ? MM : 1;
    const shape = get('shape');

    const [sw, sh] = (
      get('sheet') === '13x19' ? [13, 19] : [12, 18]
    ).map(v => v * MM);

    const w = num('width') * factor;
    const h = (
      shape === 'round' ? num('width') : num('height')
    ) * factor;

    const bleed = num('bleed') * factor;
    const gap = num('gap') * factor;
    const radius = num('radius') * factor;
    const wanted = num('quantity');
    const vulcan = get('vulcan') !== 'off';
    const transport = get('transport') !== 'off';

    const result = root.querySelector('[data-sc-result]');
    const box = root.querySelector('[data-sc-preview]');
    const button = root.querySelector('[data-sc-pdf]');

    function error(msg) {
      if (result) {
        result.textContent = msg;
        result.style.color = '#b42318';
      }
      if (box) box.innerHTML = '';
      if (button) button.disabled = true;
    }

    if (
      ![w, h, bleed, gap, radius, wanted].every(Number.isFinite) ||
      w <= 0 || h <= 0 ||
      bleed < 0 || gap < 0 ||
      radius < 0 || wanted <= 0 ||
      !Number.isInteger(wanted)
    ) {
      return error('Enter valid dimensions and quantity.');
    }

    // Round/Oval: either bleed OR gap must be at least 1 mm.
    if (
      (shape === 'round' || shape === 'oval') &&
      bleed < 1 - 1e-7 &&
      gap < 1 - 1e-7
    ) {
      return error(
        'Round and Oval stickers require at least 1 mm bleed or gap.'
      );
    }

    if (
      shape === 'rectangle' &&
      radius > Math.min(w, h) / 2
    ) {
      return error('Corner radius is too large.');
    }

    if (
      shape === 'rectangle' &&
      radius > 0 &&
      bleed < 1 &&
      gap < 1
    ) {
      return error(
        'Rounded rectangles require at least 1 mm bleed or gap.'
      );
    }

    const L = chooseLayout({
      sw, sh, w, h, bleed, gap,
      shape, vulcan, transport
    });

    if (!L) {
      return error('Sticker does not fit on this sheet.');
    }

    const required = Math.ceil(wanted / L.ups);
    const selected = QTY.find(q => q >= required);
    const cutType =
      shape === 'rectangle' && radius === 0
        ? 'line'
        : 'shape';
    const slab = CUT[cutType].find(s => s >= L.ups);

    const row = (a, b) =>
      `<div class="sc-result-row"><span>${a}</span><strong>${b}</strong></div>`;

    if (result) {
      result.style.color = '#222';
      result.innerHTML =
        row('Ups per sheet', L.ups) +
        row('Sheets required', required) +
        row('Shopify Qty option',
          selected ?? 'Request quotation') +
        row('Stickers produced',
          selected ? selected * L.ups : '—') +
        row('Cutting option', slab
          ? `${cutType === 'line' ? 'Line Cut' : 'Shape Cut'} (upto ${slab} ups)`
          : 'Request quotation') +
        row('Transport cuts', L.sections === 1
          ? 'None'
          : `${L.sections} sections (automatic)`) +
        row('Vulcan marks', vulcan ? 'ON' : 'OFF') +
        row('Arrangement', L.staggered
          ? 'Staggered'
          : L.rotated ? 'Rotated' : 'Normal') +
        '<p class="sc-note">Preview only. Aco prices are not changed yet.</p>';
    }

    Object.assign(root.dataset, {
      ups: String(L.ups),
      sheets: String(required),
      acoQty: selected ? String(selected) : '',
      cutType,
      cutSlab: slab ? String(slab) : ''
    });

    preview(root, L, radius, vulcan);

    if (button) {
      button.disabled = false;
      button.onclick = () => makePDF(L, radius, vulcan);
    }
  }

  function syncShapeFields(root) {
    const get = key =>
      root.querySelector(`[data-sc="${key}"]`);

    const shape = get('shape')?.value || 'rectangle';
    const unit = get('unit')?.value || 'mm';
    const width = get('width');
    const height = get('height');
    const bleed = get('bleed');
    const gap = get('gap');

    const isRound = shape === 'round';
    const isOval = shape === 'oval';

    root.dataset.scShape = shape;

    const widthLabel =
      root.querySelector('[data-sc-width-label]');

    if (widthLabel) {
      widthLabel.textContent = isRound
        ? 'Finished Diameter'
        : 'Finished Width';
    }

    if (isRound && width && height) {
      height.value = width.value;
    }

    const heightField =
      root.querySelector('[data-sc-height-field]');

    if (heightField) {
      heightField.style.display = isRound ? 'none' : '';
    }

    // Either bleed or gap >= 1 mm for Round/Oval.
    // If both are below 1 mm, set gap to 1 mm.
    if ((isRound || isOval) && bleed && gap) {
      const factor = unit === 'in' ? MM : 1;
      const bleedMM = Number(bleed.value) * factor;
      const gapMM = Number(gap.value) * factor;

      if (
        Number.isFinite(bleedMM) &&
        Number.isFinite(gapMM) &&
        bleedMM < 1 - 1e-7 &&
        gapMM < 1 - 1e-7
      ) {
        gap.value = unit === 'in'
          ? String(1 / MM)
          : '1';
      }
    }
  }

  function init() {
    document.querySelectorAll('[data-sticker-calculator]')
      .forEach(root => {
        if (root.dataset.scReady) return;
        root.dataset.scReady = '1';

        const refresh = () => {
          syncShapeFields(root);
          calculate(root);
        };

        root.addEventListener('input', refresh);
        root.addEventListener('change', refresh);

        refresh();
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
